/**
 * INNFLOW Design System — Sidebar 64px colapsada, ícones-only
 * ∞ verde no topo, ativo bg verde sutil, badge conversas, avatar+status rodapé
 */

import React from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  BarChart3,
  Zap,
  Settings,
  LogOut,
  Menu,
  X,
  Kanban as KanbanIcon,
  Contact,
  Brain,
  BookOpen,
  CheckSquare,
  FileText,
  User,
  DollarSign,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { usePermissions } from "@/hooks/usePermissions";
import { useSidebar } from "@/contexts/SidebarContext";
import { useTotalUnreadCount } from "@/hooks/useUnreadCount";
import { InnflowAvatar, StatusDot } from "@/components/ui/innflow";
import logoIcon from "@/assets/logo-icon.png";

interface MenuItem {
  label: string;
  icon: React.ElementType;
  path: string;
  showBadge?: boolean;
  requiresPermission?: { category: string; action: string };
}

const managerMenuItems: MenuItem[] = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  {
    label: "Conversas",
    icon: MessageSquare,
    path: "/dashboard/conversations",
    showBadge: true,
  },
  { label: "Kanban", icon: KanbanIcon, path: "/dashboard/kanban" },
  { label: "Vendas", icon: DollarSign, path: "/dashboard/sales" },
  { label: "CRM", icon: Contact, path: "/dashboard/crm" },
  { label: "Análises", icon: BarChart3, path: "/dashboard/analytics" },
  { label: "Relatórios", icon: FileText, path: "/dashboard/reports" },
  { label: "Automação", icon: Zap, path: "/dashboard/automation" },
  { label: "Treinar IA", icon: Brain, path: "/dashboard/ai-training" },
  {
    label: "Base de Conhecimento",
    icon: BookOpen,
    path: "/dashboard/knowledge",
  },
  { label: "Equipe", icon: Users, path: "/dashboard/team" },
  { label: "Configurações", icon: Settings, path: "/dashboard/settings" },
  { label: "Suporte", icon: MessageSquare, path: "/dashboard/support" },
];

const agentMenuItems: MenuItem[] = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  {
    label: "Minhas Conversas",
    icon: MessageSquare,
    path: "/dashboard/conversations",
    showBadge: true,
  },
  { label: "Meus Leads", icon: Contact, path: "/dashboard/crm" },
  { label: "Minhas Tarefas", icon: CheckSquare, path: "/dashboard/kanban" },
  { label: "Perfil", icon: User, path: "/dashboard/settings" },
];

export const Sidebar = React.memo(function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { isManager, isAdmin } = useUserRole();
  usePermissions();
  const totalUnread = useTotalUnreadCount();
  const { isOpen, toggleSidebar } = useSidebar();

  const menuItems = isManager || isAdmin ? managerMenuItems : agentMenuItems;

  const handleLogout = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const userName =
    user?.user_metadata?.name || user?.email?.split("@")[0] || "Usuário";

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden
        />
      )}

      {/* Desktop sidebar: 64px, ícones-only. Mobile: drawer overlay */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen z-50 flex flex-col",
          "sidebar-glass",
          "transition-transform duration-300 ease-out",
          "w-16",
          "lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Toggle: só mobile */}
        <button
          onClick={toggleSidebar}
          className="absolute top-4 right-2 w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 lg:hidden z-[60]"
          aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Logo INNFLOW - Avatar Sidebar */}
        <div className="flex items-center justify-center h-16 shrink-0 border-b border-white/[0.06]">
          <div className="w-10 h-10 rounded-xl bg-[rgba(25,245,140,0.1)] border border-[rgba(25,245,140,0.2)] flex items-center justify-center overflow-hidden">
            <img
              src={logoIcon}
              alt="INNFLOW Logo"
              className="w-7 h-7 object-contain"
              aria-hidden
            />
          </div>
        </div>

        {/* Menu — ícones-only, ativo = bg verde sutil */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            const showBadge =
              item.showBadge &&
              (item.path === "/dashboard/conversations"
                ? totalUnread > 0
                : true);
            const badgeCount =
              item.path === "/dashboard/conversations"
                ? totalUnread
                : item.showBadge
                  ? 3
                  : 0;

            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 1024) toggleSidebar();
                }}
                className={cn(
                  "w-10 h-10 flex flex-col items-center justify-center relative rounded-xl transition-all duration-300 hover:scale-110",
                  isActive
                    ? "sidebar-item-active text-[#19F58C]"
                    : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.05)] hover:text-white border border-transparent",
                )}
                title={item.label}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
                data-testid={`dashboard-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {showBadge && badgeCount > 0 && (
                  <span className="absolute -top-1 -right-0.5 badge-count">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Rodapé: Settings, Avatar + StatusDot, Sair */}
        <div className="shrink-0 p-2 space-y-1 border-t border-white/[0.06]">
          <button
            onClick={() => {
              navigate("/dashboard/settings");
              if (window.innerWidth < 1024) toggleSidebar();
            }}
            className={cn(
              "w-10 h-10 mx-auto flex items-center justify-center rounded-xl transition-all duration-300 hover:scale-110",
              location.pathname === "/dashboard/settings"
                ? "sidebar-item-active text-[#19F58C]"
                : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.05)] hover:text-white border border-transparent",
            )}
            title="Configurações"
            aria-label="Configurações"
          >
            <Settings className="w-5 h-5" />
          </button>

          <div className="flex items-center justify-center py-2">
            <div className="relative">
              <InnflowAvatar
                size="sm"
                color="green"
                initials={userName.slice(0, 2).toUpperCase()}
                data-testid="user-menu-btn"
              />
              <span className="absolute -bottom-0.5 -right-0.5">
                <StatusDot variant="online" size="xs" />
              </span>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl text-[rgba(255,255,255,0.6)] hover:bg-red-500/20 hover:text-red-400 transition-colors"
            title="Sair"
            aria-label="Sair"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </aside>
    </>
  );
});
